import React, { useState } from 'react';
import { TrendingDown, DollarSign } from 'lucide-react';
import { costExamples } from '../data/mock';

const CostExample = () => {
  const [selectedExample, setSelectedExample] = useState(0);
  const currentExample = costExamples[selectedExample];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50" id="pricing-example">
      <div className="container mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-[#0E3A5F] mb-2">
          What this typically looks like in real numbers
        </h2>
        <p className="text-center text-gray-600 mb-8 text-sm italic">
          Example scenarios for qualifying projects
        </p>

        {/* Example Selector */}
        <div className="flex justify-center gap-4 mb-12">
          {costExamples.map((example, index) => (
            <button
              key={index}
              onClick={() => setSelectedExample(index)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                selectedExample === index
                  ? 'bg-[#1F6FB2] text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {example.title}
            </button>
          ))}
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Current SaaS Cost */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-8 rounded-2xl border-2 border-red-200 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="h-8 w-8 text-red-600" />
                <h3 className="text-2xl font-bold text-red-900">Current SaaS Cost</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-red-700">Monthly spend:</span>
                  <span className="text-3xl font-bold text-red-900">
                    ${currentExample.monthlySaaS.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-red-700">Annual cost:</span>
                  <span className="text-2xl font-bold text-red-900">
                    ${(currentExample.monthlySaaS * 12).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Build Cost */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl border-2 border-[#1F6FB2] shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="h-8 w-8 text-[#1F6FB2]" />
                <h3 className="text-2xl font-bold text-[#0E3A5F]">Your System Build</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#1F6FB2]">Monthly payment:</span>
                  <span className="text-3xl font-bold text-[#0E3A5F]">
                    ${currentExample.monthlySaaS.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#1F6FB2]">Build duration:</span>
                  <span className="text-2xl font-bold text-[#0E3A5F]">
                    {currentExample.buildMonths} months
                  </span>
                </div>
                <div className="pt-2 border-t-2 border-[#1F6FB2]/30">
                  <div className="flex justify-between items-center">
                    <span className="text-[#1F6FB2] font-semibold">Total build:</span>
                    <span className="text-2xl font-bold text-[#0E3A5F]">
                      ${currentExample.totalBuild.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* After Launch */}
          <div className="bg-gradient-to-br from-[#7AC943]/10 to-[#7AC943]/20 p-10 rounded-2xl border-2 border-[#7AC943] shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <TrendingDown className="h-10 w-10 text-[#7AC943]" />
              <h3 className="text-3xl font-bold text-[#0E3A5F]">After Launch</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <div className="text-sm text-gray-600 mb-2">SaaS subscriptions:</div>
                <div className="text-4xl font-bold text-[#0E3A5F] line-through decoration-red-500 decoration-4">
                  ${currentExample.monthlySaaS.toLocaleString()}/mo
                </div>
                <div className="text-5xl font-bold text-[#7AC943] mt-2">$0</div>
              </div>
              
              <div>
                <div className="text-sm text-gray-600 mb-2">Maintenance only:</div>
                <div className="text-4xl font-bold text-[#1F6FB2]">
                  ${currentExample.maintenanceMonthly.toLocaleString()}/mo
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  (15% of original SaaS spend)
                </div>
              </div>
            </div>

            {/* Highlight Savings */}
            <div className="bg-white p-6 rounded-xl border-2 border-[#7AC943] text-center">
              <div className="text-lg text-gray-600 mb-2">Annual Savings</div>
              <div className="text-5xl md:text-6xl font-bold text-[#7AC943]">
                ${currentExample.annualSavings.toLocaleString()}+
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostExample;
